import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useSeason } from './useSeason';
import type { UseLanePerformanceReturn, LanePerformance } from '../types';

export function useLanePerformance(
  bowlerId: number | null,
  seasonYearOverride?: number
): UseLanePerformanceReturn {
  const { selectedSeason } = useSeason();
  const seasonYear = seasonYearOverride ?? selectedSeason;
  const [laneData, setLaneData] = useState<LanePerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchLanePerformance = useCallback(async () => {
    if (!bowlerId) {
      console.log('No bowler ID provided');
      setLaneData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('Fetching lane performance for:', { bowlerId, seasonYear });

      // Skip the database function and go straight to fallback for now
      console.log('Using fallback calculation for lane performance');
      await fetchLanePerformanceFallback(bowlerId, seasonYear);
      return;

      // Try to use the database function first (commented out for debugging)
      /*
      const { data, error: functionError } = await supabase
        .rpc('get_lane_performance', {
          target_bowler_id: bowlerId,
          season_year: currentYear,
        });

      console.log('Database function result:', { data, functionError });

      if (functionError) {
        // If function doesn't exist, fall back to manual calculation
        console.warn('Lane performance function not available, using fallback calculation');
        await fetchLanePerformanceFallback(bowlerId, currentYear);
        return;
      }

      const transformedData: LanePerformance[] = (data || []).map((lane: Record<string, unknown>) => ({
        lane_number: lane.lane_number as number,
        player_average: lane.player_average as number,
        league_average: lane.league_average as number,
        games_played: lane.games_played as number,
        performance_difference: lane.performance_difference as number,
      }));

      // Sort by performance difference (best lanes first)
      transformedData.sort((a, b) => b.performance_difference - a.performance_difference);

      setLaneData(transformedData);
      */
    } catch (err) {
      console.error('Error fetching lane performance:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch lane performance'));
    } finally {
      setLoading(false);
    }
  }, [bowlerId, seasonYear]);

  const fetchLanePerformanceFallback = async (bowlerId: number, seasonYear: number) => {
    try {
      // Helper function to calculate lane pair display
      const getLanePairDisplay = (lane: number): string => {
        if (lane % 2 === 1) {
          // Odd lane: pair is lane and lane+1
          return `${lane}/${lane + 1}`;
        } else {
          // Even lane: pair is lane-1 and lane
          return `${lane - 1}/${lane}`;
        }
      };

      // Helper function to get lane pair key for grouping
      const getLanePairKey = (lane: number): string => {
        if (lane % 2 === 1) {
          return `${lane}-${lane + 1}`;
        } else {
          return `${lane - 1}-${lane}`;
        }
      };

      // Helper function to count valid games and calculate total pins
      const calculateGameStats = (row: any) => {
        let totalPins = 0;
        let gameCount = 0;
        
        // Check each game (1-6) and only include if score_type is 'S' (normal score)
        // Ignore score_type of '0', 'A' (absent), or 'V' (vacant)
        for (let i = 1; i <= 6; i++) {
          const score = row[`score${i}`];
          const scoreType = row[`score_type${i}`];
          
          // Only count games with valid scores and score_type 'S'
          if (score && score > 0 && scoreType === 'S') {
            totalPins += score;
            gameCount++;
          }
        }
        
        return { totalPins, gameCount };
      };

      // Get player's performance by lane using individual game data
      const { data: playerData, error: playerError } = await supabase
        .from('league_bowler_week_summary')
        .select(`
          lane_bowled_on, 
          week_num,
          score1, score_type1,
          score2, score_type2,
          score3, score_type3,
          score4, score_type4,
          score5, score_type5,
          score6, score_type6
        `)
        .eq('bowler_id', bowlerId)
        .eq('season_year', seasonYear)
        .not('lane_bowled_on', 'is', null);

      console.log('Player data query result:', { playerData, playerError, bowlerId, seasonYear });

      if (playerError) {
        throw new Error(`Failed to fetch player lane data: ${playerError.message}`);
      }

      // Get league averages by lane (excluding this player)
      const { data: leagueData, error: leagueError } = await supabase
        .from('league_bowler_week_summary')
        .select(`
          lane_bowled_on, 
          score1, score_type1,
          score2, score_type2,
          score3, score_type3,
          score4, score_type4,
          score5, score_type5,
          score6, score_type6
        `)
        .neq('bowler_id', bowlerId)
        .eq('season_year', seasonYear)
        .not('lane_bowled_on', 'is', null);

      console.log('League data query result:', { leagueDataCount: leagueData?.length, leagueError });

      if (leagueError) {
        throw new Error(`Failed to fetch league lane data: ${leagueError.message}`);
      }

      // Process the data by lane pairs
      const playerLaneStats = new Map<string, { totalPins: number; totalGames: number; displayName: string }>();
      const leagueLaneStats = new Map<string, { totalPins: number; totalGames: number }>();

      // Calculate player stats by lane pair
      playerData?.forEach((row) => {
        const lane = row.lane_bowled_on;
        if (lane) {
          const lanePairKey = getLanePairKey(lane);
          const lanePairDisplay = getLanePairDisplay(lane);
          const { totalPins, gameCount } = calculateGameStats(row);
          
          console.log('Processing player row:', { 
            week: row.week_num, 
            lane, 
            lanePairKey, 
            totalPins, 
            gameCount,
            scores: [row.score1, row.score2, row.score3],
            scoreTypes: [row.score_type1, row.score_type2, row.score_type3]
          });
          
          if (gameCount > 0) {
            const current = playerLaneStats.get(lanePairKey) || { totalPins: 0, totalGames: 0, displayName: lanePairDisplay };
            playerLaneStats.set(lanePairKey, {
              totalPins: current.totalPins + totalPins,
              totalGames: current.totalGames + gameCount,
              displayName: lanePairDisplay,
            });
          }
        }
      });

      console.log('Player lane stats:', Array.from(playerLaneStats.entries()));

      // Calculate league stats by lane pair
      leagueData?.forEach((row) => {
        const lane = row.lane_bowled_on;
        if (lane) {
          const lanePairKey = getLanePairKey(lane);
          const { totalPins, gameCount } = calculateGameStats(row);
          
          if (gameCount > 0) {
            const current = leagueLaneStats.get(lanePairKey) || { totalPins: 0, totalGames: 0 };
            leagueLaneStats.set(lanePairKey, {
              totalPins: current.totalPins + totalPins,
              totalGames: current.totalGames + gameCount,
            });
          }
        }
      });

      // Combine the data
      const lanePerformance: LanePerformance[] = [];
      
      playerLaneStats.forEach((playerStats, lanePairKey) => {
        const leagueStats = leagueLaneStats.get(lanePairKey);
        if (leagueStats && leagueStats.totalGames > 0) {
          const playerAverage = playerStats.totalPins / playerStats.totalGames;
          const leagueAverage = leagueStats.totalPins / leagueStats.totalGames;
          const performanceDifference = playerAverage - leagueAverage;

          lanePerformance.push({
            lane_number: playerStats.displayName, // Use display name like "3/4" or "5/6"
            player_average: Math.round(playerAverage * 100) / 100,
            league_average: Math.round(leagueAverage * 100) / 100,
            games_played: playerStats.totalGames,
            performance_difference: Math.round(performanceDifference * 100) / 100,
          });
        }
      });

      // Sort by performance difference (best lanes first)
      lanePerformance.sort((a, b) => b.performance_difference - a.performance_difference);

      setLaneData(lanePerformance);
    } catch (err) {
      console.error('Error in lane performance fallback:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch lane performance data'));
      setLaneData([]);
    }
  };

  const refetch = async () => {
    await fetchLanePerformance();
  };

  useEffect(() => {
    fetchLanePerformance();
  }, [fetchLanePerformance]);

  return {
    laneData,
    loading,
    error,
    refetch,
    clearError: () => setError(null),
    retryLastOperation: refetch,
  };
}